/**
 * Settings UI «Видимость витрины» (FE-11 / FE-15, US-09 FR-1, DP-365).
 *
 * Доступен **только** super-admin (рендер отключается для regular).
 * Матрица «виджет × уровень территории» из `GET /api/super-admin/modules/matrix`:
 * видимость задаётся отдельно на область / район-город / сельский округ / село.
 * Колонки и подписи приходят с бэкенда (`levels`) — порядок и текст задаёт
 * сервер, на фронте не хардкодим.
 *
 * Переключатели копятся в черновике и уходят на бэк батчем по «Сохранить»
 * (PUT `/api/super-admin/modules/{id}` на каждую изменённую ячейку; если у
 * виджета изменились все уровни на одно значение — один PUT без `kato_level`)
 * с подтверждением успеха и сохранением несохранённых правок при ошибке
 * (US-09 FR-1.3, НФТ «Надёжность»).
 *
 * Витрину этот экран не гейтит: видимость у пользователя считается по
 * `GET /api/public/modules?kato=` в `ModulesContext`. Свою копию флагов
 * витрина держит в памяти, поэтому после сохранения экран сам дёргает
 * `refresh()` контекста — иначе виджет пропадал бы (или появлялся) только
 * после ручной перезагрузки страницы (US-09 FR-1.3).
 */
import { Check, Loader2 } from 'lucide-react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Switch } from '../../../components/ui/switch';
import { ModulesContext } from '../../../contexts/ModulesContext';
import { useUser } from '../../../contexts/UserContext';
import {
  getModulesMatrix,
  setModuleEnabled,
  type KatoLevelInfo,
  type ModuleLevelInfo,
} from '../../../requests/super_admin.request';

/** Порядок разделов витрины; незнакомые префиксы — после, по алфавиту. */
const SECTION_ORDER = [
  'left-menu',
  'home',
  'digital-passport',
  'analytics',
  'projects',
];

const sectionOf = (moduleId: string): string => {
  const dot = moduleId.indexOf('.');
  return dot === -1 ? moduleId : moduleId.slice(0, dot);
};

/** Ключ ячейки матрицы — пара `(module_id, kato_level)`. */
const cellKey = (moduleId: string, level: string): string => `${moduleId}|${level}`;

interface ModuleRow {
  module_id: string;
  label_ru: string | null;
  label_kz: string | null;
}

interface CellRef {
  moduleId: string;
  level: string;
}

export const ModulesAdmin = () => {
  const { t, i18n } = useTranslation();
  const { user } = useUser();
  // Мягко: без ModulesProvider (unit-тесты экрана) сохранение работает,
  // просто обновлять некому.
  const refreshShowcase = useContext(ModulesContext)?.refresh;

  const [levels, setLevels] = useState<readonly KatoLevelInfo[]>([]);
  /** Сохранённое состояние ячеек: Map<cellKey, enabled>. */
  const [saved, setSaved] = useState<ReadonlyMap<string, boolean>>(new Map());
  const [rows, setRows] = useState<readonly ModuleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Черновик: только отличия от сохранённого состояния (cellKey → enabled).
  const [draft, setDraft] = useState<ReadonlyMap<string, boolean>>(new Map());
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.is_admin ?? false;

  const applyMatrix = useCallback((cells: readonly ModuleLevelInfo[]) => {
    const nextSaved = new Map<string, boolean>();
    const byModule = new Map<string, ModuleRow>();
    for (const cell of cells) {
      nextSaved.set(cellKey(cell.module_id, cell.kato_level), cell.enabled);
      const known = byModule.get(cell.module_id);
      if (known) {
        // Подписи повторяются в строке каждого уровня — берём первую непустую.
        if (known.label_ru === null) known.label_ru = cell.label_ru;
        if (known.label_kz === null) known.label_kz = cell.label_kz;
      } else {
        byModule.set(cell.module_id, {
          module_id: cell.module_id,
          label_ru: cell.label_ru,
          label_kz: cell.label_kz,
        });
      }
    }
    setSaved(nextSaved);
    setRows(Array.from(byModule.values()));
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    getModulesMatrix()
      .then((matrix) => {
        if (cancelled) return;
        setLevels(matrix.levels);
        applyMatrix(matrix.modules);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLevels([]);
        setSaved(new Map());
        setRows([]);
        setError(true);
        console.error('modules matrix fetch failed', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, applyMatrix]);

  const sections = useMemo(() => {
    const byPrefix = new Map<string, ModuleRow[]>();
    for (const row of rows) {
      const prefix = sectionOf(row.module_id);
      const list = byPrefix.get(prefix);
      if (list) list.push(row);
      else byPrefix.set(prefix, [row]);
    }
    const order = (prefix: string) => {
      const index = SECTION_ORDER.indexOf(prefix);
      return index === -1 ? SECTION_ORDER.length : index;
    };
    return Array.from(byPrefix.entries())
      .sort(([a], [b]) => order(a) - order(b) || a.localeCompare(b))
      .map(([prefix, list]) => ({
        prefix,
        rows: [...list].sort((a, b) => a.module_id.localeCompare(b.module_id)),
      }));
  }, [rows]);

  if (!user) {
    return <p data-testid="admin-auth-required">{t('common.loading')}</p>;
  }
  if (!isAdmin) {
    return <p data-testid="admin-forbidden">{t('adminModules.forbidden')}</p>;
  }

  const isKz = i18n.language === 'kz';
  const labelOf = (row: ModuleRow) =>
    (isKz ? row.label_kz : row.label_ru) ?? row.module_id;
  const levelLabelOf = (level: KatoLevelInfo) =>
    (isKz ? level.label_kz : level.label_ru) ?? level.value;

  /**
   * Значение ячейки. Ячейки нет в матрице → показываем (fail-open):
   * «не смогли узнать» не равно «выключено».
   */
  const isOn = (moduleId: string, level: string): boolean => {
    const key = cellKey(moduleId, level);
    return draft.get(key) ?? saved.get(key) ?? true;
  };

  const onLevelsCount = (moduleId: string) =>
    levels.filter((l) => isOn(moduleId, l.value)).length;

  const cellsOf = (list: readonly ModuleRow[]): CellRef[] =>
    list.flatMap((row) => levels.map((l) => ({ moduleId: row.module_id, level: l.value })));

  const allCells = cellsOf(rows);
  const shownCount = allCells.filter((c) => isOn(c.moduleId, c.level)).length;
  const dirty = draft.size;

  const setCells = (cells: readonly CellRef[], next: boolean) => {
    setDraft((current) => {
      const map = new Map(current);
      for (const { moduleId, level } of cells) {
        const key = cellKey(moduleId, level);
        // Значение вернулось к сохранённому → правка уходит из черновика.
        if (next === (saved.get(key) ?? true)) map.delete(key);
        else map.set(key, next);
      }
      return map;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    // Несохранённое остаётся в черновике: при частичном сбое пользователь
    // видит ошибку, а его правки не теряются (НФТ «Надёжность»).
    const failed = new Map<string, boolean>();
    const done = new Map<string, boolean>();

    // Группируем черновик по виджету: изменились все уровни на одно значение —
    // один PUT без `kato_level` вместо четырёх (§3 контракта US-09).
    const byModule = new Map<string, { level: string; enabled: boolean }[]>();
    for (const [key, enabled] of draft) {
      const separator = key.lastIndexOf('|');
      const moduleId = key.slice(0, separator);
      const level = key.slice(separator + 1);
      const list = byModule.get(moduleId);
      if (list) list.push({ level, enabled });
      else byModule.set(moduleId, [{ level, enabled }]);
    }

    for (const [moduleId, edits] of byModule) {
      const wholeWidget =
        levels.length > 0 &&
        edits.length === levels.length &&
        edits.every((e) => e.enabled === edits[0].enabled);
      if (wholeWidget) {
        try {
          await setModuleEnabled(moduleId, edits[0].enabled);
          for (const e of edits) done.set(cellKey(moduleId, e.level), e.enabled);
        } catch {
          for (const e of edits) failed.set(cellKey(moduleId, e.level), e.enabled);
        }
        continue;
      }
      // Батч-сохранение — по одному PUT на ячейку: при частичном сбое видно,
      // какие именно ячейки не сохранились.
      for (const e of edits) {
        try {
          await setModuleEnabled(moduleId, e.enabled, e.level);
          done.set(cellKey(moduleId, e.level), e.enabled);
        } catch {
          failed.set(cellKey(moduleId, e.level), e.enabled);
        }
      }
    }

    // Ответ PUT приходит без `kato_level`, поэтому сохранённое состояние
    // ячейки берём из отправленного значения.
    if (done.size > 0) {
      setSaved((current) => {
        const next = new Map(current);
        for (const [key, enabled] of done) next.set(key, enabled);
        return next;
      });
    }
    // Витрина живёт на флагах `ModulesContext` (`/public/modules`), а
    // сохранение шло мимо него — без перечитывания страница показывала бы
    // старую видимость до ручного F5. Сбой refresh'а не делает сохранение
    // неуспешным: флаги обновятся при следующем фетче (смена КАТО/перезаход).
    if (done.size > 0) await refreshShowcase?.();

    setDraft(failed);
    setSaving(false);
    if (failed.size === 0) toast.success(t('adminModules.savedToast'));
    else toast.error(t('adminModules.saveError'));
  };

  // Колонка на каждый уровень + колонка «все уровни».
  const gridStyle = {
    gridTemplateColumns: `minmax(11rem,1fr) repeat(${levels.length + 1}, 6.5rem)`,
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="modules-admin">
      <div className="min-h-0 flex-1 overflow-y-auto p-5">
        <div className="max-w-5xl space-y-4">
          <div>
            <h1 className="text-2xl font-semibold">{t('adminModules.title')}</h1>
            <p className="text-sm text-text-muted">{t('adminModules.subtitle')}</p>
          </div>

          {loading && rows.length === 0 && (
            <p data-testid="modules-loading">{t('common.loading')}</p>
          )}
          {/* Ошибка загрузки ≠ пустой справочник: при упавшем запросе
              «Нет данных» вводил бы в заблуждение. */}
          {!loading && rows.length === 0 && error && (
            <p data-testid="modules-error" className="text-status-danger">
              {t('common.dataUnavailable')}
            </p>
          )}
          {!loading && rows.length === 0 && !error && (
            <p data-testid="modules-empty">{t('adminModules.emptyRegistry')}</p>
          )}

          {rows.length > 0 && (
            <>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm" data-testid="modules-summary">
                <span>
                  <span className="mr-1.5 inline-block size-2 rounded-full bg-brand-500" />
                  {t('adminModules.shown')}{' '}
                  <b className="tabular-nums" data-testid="modules-shown-count">
                    {shownCount}
                  </b>
                </span>
                <span className="text-text-muted">
                  <span className="mr-1.5 inline-block size-2 rounded-full bg-surface-border" />
                  {t('adminModules.hidden')}{' '}
                  <b className="tabular-nums" data-testid="modules-hidden-count">
                    {allCells.length - shownCount}
                  </b>
                </span>
                <span className="text-text-faint">{t('adminModules.cellsHint')}</span>
              </div>

              <div className="space-y-3.5" data-testid="modules-list">
                {sections.map(({ prefix, rows: list }) => {
                  const sectionTitle = t(`adminModules.sections.${prefix}`, {
                    defaultValue: prefix,
                  });
                  const sectionCells = cellsOf(list);
                  const onCount = sectionCells.filter((c) => isOn(c.moduleId, c.level))
                    .length;
                  return (
                    <Card
                      key={prefix}
                      className="shadow-card"
                      data-testid={`modules-section-${prefix}`}
                    >
                      <CardHeader className="flex-row items-center justify-between space-y-0 border-b border-surface-border py-4">
                        <CardTitle className="text-base">{sectionTitle}</CardTitle>
                        <span className="flex items-center gap-3">
                          <span className="text-sm tabular-nums text-text-muted">
                            {t('adminModules.widgetsCount', {
                              on: onCount,
                              total: sectionCells.length,
                            })}
                          </span>
                          <Switch
                            aria-label={t('adminModules.sectionAria', {
                              section: sectionTitle,
                            })}
                            checked={onCount > 0}
                            onCheckedChange={(next) => setCells(sectionCells, next)}
                            disabled={saving}
                            data-testid={`modules-section-toggle-${prefix}`}
                          />
                        </span>
                      </CardHeader>
                      <CardContent className="overflow-x-auto p-0">
                        <div className="min-w-[38rem]">
                          {/* Подписи уровней — с бэкенда (`levels`). */}
                          <div
                            className="grid items-end gap-2 border-b border-surface-border px-5 py-2.5"
                            style={gridStyle}
                            data-testid={`modules-levels-header-${prefix}`}
                          >
                            <span />
                            {levels.map((level) => (
                              <span
                                key={level.value}
                                title={levelLabelOf(level)}
                                data-testid={`modules-level-${level.value}`}
                                className="text-center text-xs font-medium leading-tight text-text-muted"
                              >
                                {levelLabelOf(level)}
                              </span>
                            ))}
                            <span className="text-center text-xs font-medium leading-tight text-text-faint">
                              {t('adminModules.allLevels')}
                            </span>
                          </div>
                          <ul>
                            {list.map((row) => {
                              const label = labelOf(row);
                              const on = onLevelsCount(row.module_id);
                              return (
                                <li
                                  key={row.module_id}
                                  data-testid={`module-${row.module_id}`}
                                  className="grid items-center gap-2 border-b border-surface-border px-5 py-3 last:border-0"
                                  style={gridStyle}
                                >
                                  <span className="min-w-0">
                                    <span
                                      data-testid="module-label"
                                      className="block truncate text-sm font-medium"
                                      title={label}
                                    >
                                      {label}
                                    </span>
                                    <span
                                      data-testid="module-state"
                                      className={
                                        on === 0
                                          ? 'text-xs text-text-faint'
                                          : 'text-xs text-brand-600'
                                      }
                                    >
                                      {on === 0
                                        ? t('adminModules.stateOff')
                                        : on === levels.length
                                          ? t('adminModules.stateOn')
                                          : t('adminModules.stateSome', {
                                              on,
                                              total: levels.length,
                                            })}
                                    </span>
                                  </span>
                                  {levels.map((level) => (
                                    <span key={level.value} className="flex justify-center">
                                      <Switch
                                        aria-label={t('adminModules.cellAria', {
                                          module: label,
                                          level: levelLabelOf(level),
                                        })}
                                        checked={isOn(row.module_id, level.value)}
                                        onCheckedChange={(next) =>
                                          setCells(
                                            [{ moduleId: row.module_id, level: level.value }],
                                            next,
                                          )
                                        }
                                        disabled={saving}
                                        data-testid={`module-toggle-${row.module_id}-${level.value}`}
                                      />
                                    </span>
                                  ))}
                                  {/* «Весь виджет сразу» — PUT без `kato_level`. */}
                                  <span className="flex justify-center">
                                    <Switch
                                      aria-label={t('adminModules.allLevelsAria', {
                                        module: label,
                                      })}
                                      checked={on > 0}
                                      onCheckedChange={(next) =>
                                        setCells(
                                          levels.map((l) => ({
                                            moduleId: row.module_id,
                                            level: l.value,
                                          })),
                                          next,
                                        )
                                      }
                                      disabled={saving}
                                      data-testid={`module-toggle-all-${row.module_id}`}
                                    />
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 border-t border-surface-border bg-white px-5 py-3">
        <span className="text-sm text-text-muted" data-testid="modules-dirty">
          {dirty === 0
            ? t('adminModules.noChanges')
            : t('adminModules.changes', { count: dirty })}
        </span>
        <span className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            disabled={dirty === 0 || saving}
            data-testid="modules-reset"
            onClick={() => setDraft(new Map())}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={dirty === 0 || saving}
            data-testid="modules-save"
            onClick={() => void handleSave()}
          >
            {saving ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <Check className="mr-2 size-4" />
            )}
            {t('common.save')}
          </Button>
        </span>
      </div>
    </div>
  );
};
